import { TreeDataItem as BaseTreeDataItem } from "@/components/tree-view";
import { EmailFolder } from "@cohesive/api-entities";
import {
  Archive,
  FileEdit,
  Inbox,
  LucideProps,
  Mail,
  Package,
  Send,
  Star,
  Tag,
  Trash,
} from "lucide-react";
import React from "react";

// Use type alias instead of interface extension to avoid type conflicts
export type TreeDataItem = BaseTreeDataItem & {
  labelType?: string;
};

// Define the type for TreeSVGIcon to match the TreeView component
export type TreeSVGIcon = React.ForwardRefExoticComponent<
  Omit<LucideProps, "ref"> & React.RefAttributes<SVGSVGElement>
>;

export type MessageTabConfig = {
  type: "message";
  label: string;
  icon: React.ReactNode;
};

export type TabConfig = MessageTabConfig;

// Define the desired order for system labels
export const DEFAULT_FOLDERS_ORDER = [
  "INBOX",
  "STARRED",
  "SENT",
  "DRAFT",
  "TRASH",
  "SPAM",
  "IMPORTANT",
  "ALL_MAIL",
];

export const getMailFolderIcon = (
  label: string,
): React.ForwardRefExoticComponent<
  Omit<LucideProps, "ref"> & React.RefAttributes<SVGSVGElement>
> => {
  switch (label) {
    case "INBOX":
      return Inbox;
    case "STARRED":
      return Star;
    case "SENT":
      return Send;
    case "DRAFT":
      return FileEdit;
    case "TRASH":
      return Trash;
    case "SPAM":
      return Archive;
    case "ALL_MAIL":
      return Mail;
    case "UNREAD":
      return Package;
    default:
      return Tag;
  }
};

// Function to parse nested label paths into a tree structure
export interface LabelTreeNode {
  id: string;
  name: string;
  fullPath: string;
  type: string;
  children: LabelTreeNode[];
  parent?: LabelTreeNode;
  messagesTotal: number;
}

export function buildLabelTree(labels: EmailFolder[]): LabelTreeNode[] {
  const rootNodes: LabelTreeNode[] = [];
  const nodeMap = new Map<string, LabelTreeNode>();

  // First pass: create all nodes
  labels.forEach((label) => {
    const paths = label.name.split("/");
    const name = paths[paths.length - 1];

    const node: LabelTreeNode = {
      id: label.id,
      name,
      fullPath: label.name,
      type: label.type,
      children: [],
      messagesTotal: label.total_items,
    };

    nodeMap.set(label.name, node);
  });

  // Second pass: build the tree structure
  labels.forEach((label) => {
    const node = nodeMap.get(label.name);
    if (!node) return;

    const paths = label.name.split("/");

    if (paths.length === 1) {
      // This is a root node
      rootNodes.push(node);
    } else {
      // This has a parent
      const parentPath = paths.slice(0, -1).join("/");
      const parentNode = nodeMap.get(parentPath);

      if (parentNode) {
        parentNode.children.push(node);
        node.parent = parentNode;
      } else {
        // Parent doesn't exist in our label list, create a virtual parent
        const virtualParent: LabelTreeNode = {
          id: `virtual-${parentPath}`,
          name: paths[paths.length - 2],
          fullPath: parentPath,
          type: "USER",
          children: [node],
          messagesTotal: 0,
        };

        node.parent = virtualParent;
        nodeMap.set(parentPath, virtualParent);

        // Check if the virtual parent has a parent
        if (paths.length > 2) {
          const grandparentPath = paths.slice(0, -2).join("/");
          const grandparent = nodeMap.get(grandparentPath);
          if (grandparent) {
            grandparent.children.push(virtualParent);
            virtualParent.parent = grandparent;
          } else {
            rootNodes.push(virtualParent);
          }
        } else {
          rootNodes.push(virtualParent);
        }
      }
    }
  });

  return rootNodes;
}
